import api from './axiosConfig';

// ファイルタイプの定義
const EXCEL_MIME_TYPES = [
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/csv'
];

const POWERPOINT_MIME_TYPES = [
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.oasis.opendocument.presentation'
];

const IMAGE_MIME_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/svg+xml',
  'image/bmp'
];

const VIDEO_MIME_TYPES = [
  'video/mp4',
  'video/mpeg',
  'video/quicktime',
  'video/x-msvideo',
  'video/x-ms-wmv',
  'video/webm'
];

const PDF_MIME_TYPES = [
  'application/pdf'
];

const DOCUMENT_MIME_TYPES = [
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
  'text/rtf'
];

// ファイルサイズ制限
const MAX_FILE_SIZES = {
  excel: 10 * 1024 * 1024, // 10MB
  powerpoint: 20 * 1024 * 1024, // 20MB
  image: 10 * 1024 * 1024, // 10MB
  video: 1024 * 1024 * 1024 // 1GB
};

class FileUploadService {
  /**
   * Excelファイルかどうかを判定
   */
  isExcelFile(file: File): boolean {
    // MIMEタイプで判定
    if (EXCEL_MIME_TYPES.includes(file.type)) {
      return true;
    }
    
    // ファイル拡張子でも判定
    const extension = file.name.split('.').pop()?.toLowerCase();
    return ['xlsx', 'xls', 'csv'].includes(extension || '');
  }

  /**
   * PowerPointファイルかどうかを判定
   */
  isPowerPointFile(file: File): boolean {
    // MIMEタイプで判定
    if (POWERPOINT_MIME_TYPES.includes(file.type)) {
      return true;
    }
    
    // ファイル拡張子でも判定
    const extension = file.name.split('.').pop()?.toLowerCase();
    return ['pptx', 'ppt', 'odp'].includes(extension || '');
  }

  /**
   * 画像ファイルかどうかを判定
   */
  isImageFile(file: File): boolean {
    // MIMEタイプで判定
    if (IMAGE_MIME_TYPES.includes(file.type)) {
      return true;
    }
    
    // ファイル拡張子でも判定
    const extension = file.name.split('.').pop()?.toLowerCase();
    return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(extension || '');
  }

  /**
   * 動画ファイルかどうかを判定
   */
  isVideoFile(file: File): boolean {
    // MIMEタイプで判定
    if (VIDEO_MIME_TYPES.includes(file.type)) {
      return true;
    }
    
    // ファイル拡張子でも判定
    const extension = file.name.split('.').pop()?.toLowerCase();
    return ['mp4', 'mpeg', 'mpg', 'mov', 'avi', 'wmv', 'webm'].includes(extension || '');
  }

  /**
   * PDFファイルかどうかを判定
   */
  isPDFFile(file: File): boolean {
    // MIMEタイプで判定
    if (PDF_MIME_TYPES.includes(file.type)) {
      return true;
    }
    
    // ファイル拡張子でも判定
    const extension = file.name.split('.').pop()?.toLowerCase();
    return extension === 'pdf';
  }

  /**
   * その他のドキュメントファイルかどうかを判定
   */
  isDocumentFile(file: File): boolean {
    // MIMEタイプで判定
    if (DOCUMENT_MIME_TYPES.includes(file.type)) {
      return true;
    }
    
    // ファイル拡張子でも判定
    const extension = file.name.split('.').pop()?.toLowerCase();
    return ['doc', 'docx', 'txt', 'rtf'].includes(extension || '');
  }

  /**
   * Excelファイルのバリデーション
   */
  validateExcelFile(file: File): void {
    if (!this.isExcelFile(file)) {
      throw new Error('対応していないファイル形式です。Excel形式（.xlsx, .xls, .csv）のファイルを選択してください。');
    }
    
    if (file.size > MAX_FILE_SIZES.excel) {
      throw new Error('ファイルサイズが大きすぎます（最大: 10MB）');
    }
  }

  /**
   * PowerPointファイルのバリデーション
   */
  validatePowerPointFile(file: File): void {
    if (!this.isPowerPointFile(file)) {
      throw new Error('対応していないファイル形式です。PowerPoint形式（.pptx, .ppt, .odp）のファイルを選択してください。');
    }
    
    if (file.size > MAX_FILE_SIZES.powerpoint) {
      throw new Error('ファイルサイズが大きすぎます（最大: 20MB）');
    }
  }

  /**
   * 画像ファイルのバリデーション
   */
  validateImageFile(file: File): void {
    if (!this.isImageFile(file)) {
      throw new Error('対応していないファイル形式です。画像形式（.jpg, .png, .gif等）のファイルを選択してください。');
    }
    
    if (file.size > MAX_FILE_SIZES.image) {
      throw new Error('ファイルサイズが大きすぎます（最大: 10MB）');
    }
  }

  /**
   * ファイルに応じたAPIエンドポイントを取得
   */
  getEndpointForFile(file: File): string {
    if (this.isExcelFile(file)) {
      return '/fileio/excel/convert-to-message';
    }
    
    if (this.isPowerPointFile(file)) {
      return '/fileio/ppt/input/convert-to-message';
    }
    
    throw new Error('対応していないファイル形式です');
  }

  /**
   * Excelファイルをチャットメッセージに変換
   */
  async convertExcelToMessage(file: File, prompt?: string): Promise<string> {
    try {
      // バリデーション
      this.validateExcelFile(file);
      
      // FormDataを作成
      const formData = new FormData();
      formData.append('file', file);
      if (prompt) {
        formData.append('prompt', prompt);
      }
      
      // APIリクエスト
      const response = await api.post('/fileio/excel/convert-to-message', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // レスポンスチェック
      if (!response.data.success) {
        throw new Error(response.data.message || 'ファイルの処理に失敗しました');
      }
      
      return response.data.data.message;
      
    } catch (error: any) {
      // エラーハンドリング
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      
      if (error.message === 'Network Error') {
        throw new Error('ファイルの処理中にエラーが発生しました');
      }
      
      throw error;
    }
  }

  /**
   * PowerPointファイルをチャットメッセージに変換
   */
  async convertPowerPointToMessage(file: File, prompt?: string): Promise<string> {
    try {
      // バリデーション
      this.validatePowerPointFile(file);
      
      // FormDataを作成
      const formData = new FormData();
      formData.append('file', file);
      if (prompt) {
        formData.append('prompt', prompt);
      }
      
      // APIリクエスト
      const response = await api.post('/fileio/ppt/input/convert-to-message', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      // レスポンスチェック
      if (!response.data.success) {
        throw new Error(response.data.message || 'ファイルの処理に失敗しました');
      }
      
      return response.data.data.message;
      
    } catch (error: any) {
      // エラーハンドリング
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      
      if (error.message === 'Network Error') {
        throw new Error('ファイルの処理中にエラーが発生しました');
      }
      
      throw error;
    }
  }

  /**
   * ファイルをチャットメッセージに変換（統合メソッド）
   */
  async convertFileToMessage(file: File, prompt?: string): Promise<string> {
    if (this.isExcelFile(file)) {
      // Excel/CSVファイルをバックエンドで変換して、実際のデータを返す
      const convertedMessage = await this.convertExcelToMessage(file, prompt);
      // ファイル内容を[document]タグで囲む
      const icon = this.getFileIcon(file);
      const formattedMessage = `${icon} ${file.name}\n\n[document]\n${convertedMessage}\n[/document]`;
      return formattedMessage;
    }
    
    if (this.isPowerPointFile(file)) {
      // PowerPointファイルをバックエンドで変換して、実際のデータを返す
      const convertedMessage = await this.convertPowerPointToMessage(file, prompt);
      // ファイル内容を[document]タグで囲む
      const icon = this.getFileIcon(file);
      const formattedMessage = `${icon} ${file.name}\n\n[document]\n${convertedMessage}\n[/document]`;
      return formattedMessage;
    }
    
    throw new Error('対応していないファイル形式です');
  }

  /**
   * ファイルのシンプルな要約を生成（ファイル名とアイコンのみ）
   */
  getSimpleFileSummary(file: File, prompt?: string): string {
    const icon = this.getFileIcon(file);
    const fileName = file.name;
    
    let summary = `${icon} ${fileName}`;
    
    if (prompt) {
      summary += `\n\n${prompt}`;
    }
    
    return summary;
  }

  /**
   * サポートされているファイルかどうかを判定
   */
  isSupportedDocumentFile(file: File): boolean {
    return this.isExcelFile(file) || 
           this.isPowerPointFile(file) || 
           this.isImageFile(file) || 
           this.isVideoFile(file) || 
           this.isPDFFile(file) || 
           this.isDocumentFile(file);
  }

  /**
   * ファイルサイズのフォーマット
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  }

  /**
   * ファイルアイコンを取得
   */
  getFileIcon(file: File): string {
    if (this.isExcelFile(file)) {
      return '📊'; // Excel
    }
    
    if (this.isPowerPointFile(file)) {
      return '📑'; // PowerPoint
    }
    
    if (file.type.startsWith('image/')) {
      return '🖼️'; // 画像
    }
    
    if (file.type.startsWith('video/')) {
      return '🎬'; // 動画
    }
    
    return '📄'; // その他
  }

  /**
   * ファイル拡張子を取得
   */
  getFileExtension(file: File): string {
    return file.name.split('.').pop()?.toUpperCase() || '';
  }
}

// AI連携用のファイルアップロード機能
export interface AIFileUploadResponse {
  success: boolean;
  file_id?: string;
  service?: string;
  metadata?: {
    file_name: string;
    file_size: number;
    mime_type: string;
  };
  s3_url?: string;
  s3_key?: string;
  s3_bucket?: string;
  error?: string;
}

/**
 * AI連携用のファイルアップロード（リトライ・タイムアウト対応）
 */
export const uploadFile = async (
  file: File,
  aiModel: string,
  chatId: number,
  onProgress?: (progress: number) => void,
  options: {
    maxRetries?: number;
    timeout?: number;
    retryDelay?: number;
  } = {}
): Promise<AIFileUploadResponse> => {
  const { maxRetries = 3, timeout = 300000, retryDelay = 1000 } = options;
  
  const performUpload = async (attempt: number = 1): Promise<AIFileUploadResponse> => {
    try {
      // FormDataを作成
      const formData = new FormData();
      formData.append('file', file);
      formData.append('ai_model', aiModel);
      formData.append('chat_id', chatId.toString());
      // APIリクエスト
      const response = await api.post('/file-upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        timeout,
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress(progress);
          }
        }
      });
      return response.data;

    } catch (error: any) {
      console.error('❌ uploadFile: Error occurred', {
        error_message: error.message,
        error_response: error.response?.data,
        error_status: error.response?.status,
        error_details: error,
        request_data: {
          file_name: file.name,
          file_size: file.size,
          file_type: file.type,
          ai_model: aiModel,
          chat_id: chatId
        }
      });
      
      // エラーレスポンスの詳細をログ出力
      if (error.response?.data) {
        console.error('❌ Server Error Response:', error.response.data);
        if (error.response.data.errors) {
          console.error('❌ Validation Errors:', error.response.data.errors);
        }
      }
      
      // タイムアウトエラーの場合
      if (error.name === 'TimeoutError' || error.code === 'ECONNABORTED') {
        if (attempt < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, retryDelay * attempt));
          return performUpload(attempt + 1);
        }
        return {
          success: false,
          error: 'Request timeout'
        };
      }

      // ネットワークエラーの場合
      if (error.message === 'Network Error' && attempt < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, retryDelay * attempt));
        return performUpload(attempt + 1);
      }

      // その他のエラーハンドリング
      if (error.response?.data) {
        return {
          success: false,
          error: error.response.data.error || error.response.data.message || 'ファイルアップロードに失敗しました'
        };
      }

      return {
        success: false,
        error: error.message || 'ファイルアップロードに失敗しました'
      };
    }
  };

  return performUpload();
};

/**
 * ファイル削除
 */
export const deleteFile = async (
  fileId: string,
  service: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    const response = await api.delete(`/file-upload/${fileId}`, {
      data: { service }
    });

    return response.data;

  } catch (error: any) {
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'ファイル削除に失敗しました'
    };
  }
};

/**
 * ファイルステータス取得
 */
export const getFileStatus = async (
  fileId: string,
  service: string
): Promise<{ success: boolean; status?: any; error?: string }> => {
  try {
    const response = await api.get(`/file-upload/${fileId}/status?service=${service}`);

    return {
      success: true,
      status: response.data
    };

  } catch (error: any) {
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'ステータス取得に失敗しました'
    };
  }
};

/**
 * チャンクアップロード対応（大容量ファイル用）
 */
export const uploadLargeFile = async (
  file: File,
  aiModel: string,
  chatId: number,
  onProgress?: (progress: number) => void,
  chunkSize: number = 1024 * 1024 * 10 // 10MB chunks
): Promise<AIFileUploadResponse> => {
  // ファイルサイズが閾値以下の場合は通常のアップロード
  if (file.size <= chunkSize) {
    return uploadFile(file, aiModel, chatId, onProgress);
  }

  try {
    // チャンク分割してアップロード
    const totalChunks = Math.ceil(file.size / chunkSize);
    let uploadedBytes = 0;

    for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
      const start = chunkIndex * chunkSize;
      const end = Math.min(start + chunkSize, file.size);
      const chunk = file.slice(start, end);

      const formData = new FormData();
      formData.append('file', chunk);
      formData.append('ai_model', aiModel);
      formData.append('chat_id', chatId.toString());
      formData.append('chunk_index', chunkIndex.toString());
      formData.append('total_chunks', totalChunks.toString());
      formData.append('original_filename', file.name);

      const response = await api.post('/file-upload/chunk', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const chunkProgress = (progressEvent.loaded / progressEvent.total) * (chunk.size / file.size);
            const totalProgress = ((uploadedBytes + progressEvent.loaded) / file.size) * 100;
            onProgress(Math.round(totalProgress));
          }
        }
      });

      uploadedBytes += chunk.size;

      // 最後のチャンクの場合、ファイル統合が完了するまで待機
      if (chunkIndex === totalChunks - 1) {
        return response.data;
      }
    }

    return {
      success: false,
      error: 'チャンクアップロードが完了しませんでした'
    };

  } catch (error: any) {
    return {
      success: false,
      error: error.response?.data?.error || error.message || 'チャンクアップロードに失敗しました'
    };
  }
};

export const fileUploadService = new FileUploadService();